import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plug } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';
import { ApiError } from '../../lib/api';
import * as oauthApi from '../../lib/oauthApi';

// ---------------------------------------------------------------------------
// Firmio MCP — „Aplicații conectate”: the MCP clients (Claude Desktop,
// claude.ai, Claude Code, Cursor…) that hold a live token for this person, with
// a disconnect button. Disconnecting revokes every token that client holds; the
// next call from it fails and it has to ask for access again. Shown on the
// profile page and on /mcp.
// ---------------------------------------------------------------------------

function formatWhen(iso: string | null, locale: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(locale === 'en' ? 'en-GB' : 'ro-RO', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

export function ConnectedAppsSection({ showGuideLink = false }: { showGuideLink?: boolean }) {
  const { t, locale } = useI18n();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const apps = useQuery({ queryKey: ['oauth-connections'], queryFn: oauthApi.listConnectedApps });
  const revoke = useMutation({
    mutationFn: (clientId: string) => oauthApi.revokeConnectedApp(clientId),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['oauth-connections'] });
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : t('profile.connectedApps.revokeFailed')),
  });

  return (
    <section className="mt-5 rounded-lg border border-border bg-card p-5 shadow-sm">
      <div className="mb-3 flex items-center gap-2">
        <Plug size={18} className="text-muted-foreground" />
        <div>
          <h2 className="text-sm font-semibold text-foreground">
            {t('profile.connectedApps.title')}
          </h2>
          <p className="text-xs text-muted-foreground">{t('profile.connectedApps.subtitle')}</p>
        </div>
      </div>

      {showGuideLink && (
        <p className="mb-3 text-xs text-muted-foreground">
          <Link to="/mcp" className="text-primary underline">
            {t('profile.connectedApps.guideLink')}
          </Link>
        </p>
      )}

      {apps.isLoading && <p className="text-sm text-muted-foreground">{t('common.loading')}</p>}
      {apps.isError && (
        <p className="text-sm text-red-600">{t('profile.connectedApps.loadFailed')}</p>
      )}
      {apps.data && apps.data.length === 0 && (
        <p className="text-sm text-muted-foreground">{t('profile.connectedApps.empty')}</p>
      )}
      {apps.data && apps.data.length > 0 && (
        <ul className="divide-y divide-border">
          {apps.data.map((app) => (
            <li key={app.clientId} className="flex items-center gap-3 py-3">
              {app.logoUri ? (
                <img
                  src={app.logoUri}
                  alt=""
                  className="h-8 w-8 shrink-0 rounded-md object-cover"
                />
              ) : (
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <Plug size={16} />
                </span>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foreground">
                  {app.clientName ?? t('profile.connectedApps.unknownClient')}
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {t('profile.connectedApps.connectedAt')} {formatWhen(app.connectedAt, locale)}
                  {' · '}
                  {t('profile.connectedApps.lastUsed')} {formatWhen(app.lastUsedAt, locale)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => revoke.mutate(app.clientId)}
                disabled={revoke.isPending}
                className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted disabled:opacity-50"
              >
                {t('profile.connectedApps.disconnect')}
              </button>
            </li>
          ))}
        </ul>
      )}
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </section>
  );
}
